import React, {useState, useEffect} from "react";

// Redux
import {useSelector, useDispatch} from "react-redux";
import {
  fetchEventOrganizer,
  editEventOrganizer,
  deleteEventOrganizer,
} from "../../../redux/features/events/organizersSlice";

// Routing
import {useParams, useHistory, Redirect} from "react-router-dom";

// Components
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Paper from "../../UI/MainPaper";
import EventOrganizersForm from "./EventOrganizersForm";
import FullContainerProgress from "../../UI/FullContainerProgress";
import DeleteButton from "../../Buttons/DeleteButton";
import DeleteDialog from "../../UI/DeleteDialog";
import BreadcrumbsWithEdit from "../../Navigation/BreadcrumbsWithEdit";

const EventOrganizersEdit = () => {
  // Local State
  const [isEditMode, setEditMode] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Routing
  const {id} = useParams();
  const history = useHistory();

  // Redux
  const dispatch = useDispatch();
  const {currentPageEventOrganizer, isLoading, error} = useSelector(
    state => state.eventOrganizers
  );
  const {userPermission} = useSelector(state => {
    return state.auth;
  });
  const {write, delete: deletePermission} = userPermission.events;

  const formattedValues = {
    name: currentPageEventOrganizer.name,
    email: currentPageEventOrganizer.email,
    phone: currentPageEventOrganizer.phone,
    background: currentPageEventOrganizer.background,
    website: currentPageEventOrganizer.website,
  };

  let breadcrumbNameMap = {
    "/events": "Events",
    "/events/organizers": "Event Organizers",
  };
  breadcrumbNameMap[`/events/organizers/${id}`] =
    currentPageEventOrganizer.name;

  useEffect(() => {
    // check if the id is a int to keep going
    // This if is why javascript is broken :)
    if (Number.isInteger(id * 1) && id * 1 < Number.MAX_SAFE_INTEGER) {
      dispatch(fetchEventOrganizer(id));
    } else {
      history.push("/404");
    }
  }, [dispatch, history, id]);

  useEffect(() => {
    setEditMode(write);
  }, [write]);

  const onSubmit = async values => {
    const eventOrganizerObj = {
      organizer_id: id,
      ...values,
    };
    // Logic to show error or success
    const res = await dispatch(editEventOrganizer(eventOrganizerObj));
    if (!res.error) {
      history.push({
        pathname: "/events/organizers",
        state: {
          showSuccess: true,
          snackbarContent: `Your changes to ${eventOrganizerObj.name} were saved successfully!`,
          snackbarLink: `/events/organizers/${eventOrganizerObj.organizer_id}`,
        },
      });
    }
  };

  const onDelete = async () => {
    // Logic to show error or success
    const res = await dispatch(deleteEventOrganizer(id));
    if (!res.error) {
      history.push({
        pathname: "/events/organizers",
        state: {
          showSuccess: true,
          snackbarContent: `The Event Organizer was successfully deleted!`,
        },
      });
    }
  };

  // Redirect if organizer doesn't exist'
  if (error && error.errorCode === 404) {
    return <Redirect to={"/404"} />;
  }

  return (
    <>
      <Grid container spacing={2}>
        <BreadcrumbsWithEdit
          isLoading={isLoading}
          breadcrumbNameMap={breadcrumbNameMap}
          isEditMode={isEditMode}
          setEditMode={setEditMode}
          writePermission={write}
        />
        <Grid item xs={12}>
          <Paper>
            {isLoading ? (
              <FullContainerProgress />
            ) : (
              <>
                <Typography component="h1" variant="h5" gutterBottom>
                  {currentPageEventOrganizer.name}
                </Typography>
                <EventOrganizersForm
                  defaultValues={formattedValues}
                  readOnly={!isEditMode}
                  submitButtonLabel="Save Changes"
                  onSubmit={onSubmit}
                  onCancel={() => setEditMode(false)}
                />
              </>
            )}
          </Paper>
        </Grid>
        {isEditMode && !isLoading && !!deletePermission && (
          <Grid item xs={12}>
            <DeleteButton onClick={() => setDialogOpen(true)} size="large">
              Delete Event Organizer
            </DeleteButton>
          </Grid>
        )}
      </Grid>
      {!!deletePermission && (
        <DeleteDialog
          dialogTitle="Are you sure you want to delete this Event Organizer?"
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSubmit={() => onDelete()}
        />
      )}
    </>
  );
};

export default EventOrganizersEdit;
